import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./Auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceRequests = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/requests`);
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/requests/${requestId}/status`, { status: newStatus });
      fetchRequests();
    } catch (error) {
      console.error("Failed to update status:", error);
      alert("Failed to update status");
    }
  };

  const handleAccept = async (requestId) => {
    try {
      await axios.post(`${API}/requests/${requestId}/accept`);
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to accept request");
    }
  };

  const handleDecline = async (requestId) => {
    try {
      await axios.post(`${API}/requests/${requestId}/decline`);
      fetchRequests();
    } catch (error) {
      alert("Failed to decline request");
    }
  };

  const getStatusBadge = (status) => {
    return <span className={`status-badge status-${status}`}>{status.replace("_", " ")}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const getTitle = () => {
    if (user?.role === "customer") return "My Requests";
    if (user?.role === "provider") return "My Jobs";
    return "All Requests";
  };

  const getSubtitle = () => {
    if (user?.role === "customer") return "Track your service requests";
    if (user?.role === "provider") return "Manage your assigned jobs";
    return "Monitor all service activity";
  };

  return (
    <div className="requests-screen" data-testid="service-requests">
      <div className="requests-header">
        <h1 className="requests-title">{getTitle()}</h1>
        <p className="requests-subtitle">{getSubtitle()}</p>
      </div>

      {requests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-text">No requests yet</div>
        </div>
      ) : (
        <div>
          {requests.map((request) => (
            <div key={request.id} className="request-card" data-testid={`job-item-${request.id}`}>
              <div className="request-header">
                <div>
                  <div className="request-service">
                    {request.urgency === "emergency" && "🚨 "}
                    {request.service_category}
                  </div>
                  <div className="request-customer">
                    {user?.role === "customer" 
                      ? request.selected_provider_name || request.assigned_provider_name || "Pending assignment"
                      : request.customer_name
                    }
                  </div>
                </div>
                {getStatusBadge(request.status)}
              </div>

              <div className="request-details">
                <p style={{ marginBottom: '8px' }}>{request.description}</p>
                <p style={{ marginBottom: '8px' }}>📍 {request.location || request.customer_address}</p>
                <p style={{ marginBottom: '8px' }}>💰 ${request.total_cost?.toFixed(2)}</p>
                <p style={{ fontSize: '11px', color: 'var(--muted)' }}>
                  {formatDate(request.created_at)}
                </p>
              </div>

              {/* Provider Actions */}
              {user?.role === "provider" && request.status === "pending" && (
                <div className="request-actions">
                  <button 
                    className="action-btn action-btn-primary"
                    onClick={() => handleAccept(request.id)}
                  >
                    Accept Job
                  </button>
                  <button 
                    className="action-btn action-btn-danger"
                    onClick={() => handleDecline(request.id)}
                  >
                    Decline
                  </button>
                </div>
              )}

              {user?.role === "provider" && request.status === "assigned" && (
                <div className="request-actions">
                  <button 
                    className="action-btn action-btn-primary"
                    onClick={() => handleStatusUpdate(request.id, "en_route")}
                  >
                    I'm On My Way
                  </button>
                </div>
              )}

              {user?.role === "provider" && request.status === "en_route" && (
                <div className="request-actions">
                  <button 
                    className="action-btn action-btn-primary"
                    onClick={() => handleStatusUpdate(request.id, "in_progress")}
                  >
                    Start Work
                  </button>
                </div>
              )}

              {user?.role === "provider" && request.status === "in_progress" && (
                <div className="request-actions">
                  <button 
                    className="action-btn action-btn-primary"
                    onClick={() => handleStatusUpdate(request.id, "completed")}
                  >
                    Mark Complete
                  </button>
                </div>
              )}

              {/* Admin Actions */}
              {user?.role === "admin" && request.status === "requested" && (
                <div className="request-actions">
                  <button 
                    className="action-btn action-btn-secondary"
                    onClick={() => handleStatusUpdate(request.id, "cancelled")}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceRequests;
